import { useCallback, useEffect, useRef, useState } from 'react';
import { apiError } from '../../Documents/format';
import { isCancelled, isForbidden } from '../journalApi';

export type Loader<T> = (silent: boolean, signal: AbortSignal) => Promise<T>;

/**
 * Загрузка одного представления аналитики (или сводки).
 *  - key описывает запрос целиком (фильтры + параметры вида): при его смене - новый запрос;
 *  - повтор с тем же key («Обновить», «Повторить») уходит с silent и в журнал действий не пишется;
 *  - load = null - вид скрыт: запросов нет; при возврате к виду с тем же key и без «Обновить» данные не перезапрашиваются;
 *  - устаревшие ответы отбрасываются, прежние данные остаются на экране до прихода новых.
 */
export function useLoad<T>(load: Loader<T> | null, key: string, reloadToken: number, fallback: string) {
  const [data, setData] = useState<T | null>(null);
  const [dataKey, setDataKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{ text: string; forbidden: boolean } | null>(null);
  const [retryToken, setRetryToken] = useState(0);
  const loader = useRef(load);
  loader.current = load;
  const seq = useRef(0);
  const audited = useRef<string | null>(null);
  /** Запрос, данные которого уже на экране: key + номер «Обновить» + номер «Повторить» */
  const loaded = useRef<string | null>(null);
  const enabled = load !== null;

  useEffect(() => {
    const run = loader.current;
    if (!run) return undefined;
    const signature = `${key}#${reloadToken}#${retryToken}`;
    if (loaded.current === signature) return undefined;
    const current = ++seq.current;
    const controller = new AbortController();
    const silent = audited.current === key;
    setLoading(true);

    run(silent, controller.signal)
      .then(res => {
        if (current !== seq.current) return;
        // отмечается только после ответа: отменённый запрос (в т.ч. двойной запуск эффекта в dev) просмотр не «съедает»
        audited.current = key;
        loaded.current = signature;
        setData(res);
        setDataKey(key);
        setError(null);
      })
      .catch(err => {
        if (isCancelled(err) || current !== seq.current) return;
        setError({ text: apiError(err, fallback), forbidden: isForbidden(err) });
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });

    return () => controller.abort();
    // loader читается из ref: запрос описывает key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key, reloadToken, retryToken]);

  const retry = useCallback(() => setRetryToken(t => t + 1), []);

  /** stale - на экране данные прежнего запроса (идёт загрузка нового) */
  return { data, loading, error, retry, stale: dataKey !== key };
}
